import React, { useState, useEffect } from 'react';
import { LogOut, Moon, Sun, Edit2, Shield, BookOpen, ClipboardCheck } from 'lucide-react';
import { useAppDataContext } from '../context/AppDataContext';
import { userKey } from '../lib/deviceStore';
import { supabase } from '../supabaseClient';
import { LEVELS } from '../constants/levels';
import Critter from '../components/glyphs/Critter';
import { CRITTERS } from '../components/glyphs/critters';

// New Component for dynamic Therapist fetching
function TherapistCard({ therapistId, isDarkMode }: { therapistId?: string, isDarkMode: boolean }) {
    const [therapistName, setTherapistName] = useState('טוען...');

    useEffect(() => {
        if (!therapistId) {
            setTherapistName('לא משויך מטפל');
            return;
        }

        const fetchTherapist = async () => {
            const { data } = await supabase
                .from('profiles')
                .select('full_name') // Remove email, it likely doesn't exist or is restricted
                .eq('id', therapistId)
                .single();

            if (data) {
                setTherapistName(data.full_name ? `ד"ר ${data.full_name}` : 'המטפל שלך');
            } else {
                setTherapistName('מטפל לא נמצא');
            }
        };

        fetchTherapist();
    }, [therapistId]);

    return (
        <div className={`p-4 rounded-xl flex items-center justify-between mt-8 ${!isDarkMode ? 'bg-primary/10 border-primary/20' : 'bg-muted/50 border border-border'}`}>
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center">
                    <Shield size={24} />
                </div>
                <div>
                    <span className="block font-bold">המטפל שלך</span>
                    <span className={`text-xs ${!isDarkMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>{therapistName}</span>
                </div>
            </div>
        </div>
    );
}

// מיפוי אווטארים (אותו מיפוי כמו בדשבורד)
type DiaryEntry = { id: number; date: string; text: string; title?: string };
/** Written to localStorage by completeMission in AppDataContext. */
type MissionHistoryEntry = { id: number; title: string; xp: number; date: string };

export default function PatientProfile() {
    const { profile, updateAvatar } = useAppDataContext();
    const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark based on design
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [showDiary, setShowDiary] = useState(false);

    // History / Completed Missions State
    const [showHistory, setShowHistory] = useState(false);
    const [historyEntries, setHistoryEntries] = useState<MissionHistoryEntry[]>([]);

    // Diary State
    const [addingEntry, setAddingEntry] = useState(false);
    const [newEntryTitle, setNewEntryTitle] = useState('');
    const [newEntryText, setNewEntryText] = useState('');
    const [diaryEntries, setDiaryEntries] = useState<DiaryEntry[]>([]);
    const [selectedEntry, setSelectedEntry] = useState<DiaryEntry | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const loadDiary = () => {
        if (!profile) return;
        const saved = localStorage.getItem(userKey('remind_diary', profile.id));
        if (saved) {
            try {
                setDiaryEntries((JSON.parse(saved) as DiaryEntry[]).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
            } catch (e) { console.error('Failed to load diary', e); }
        }
        setAddingEntry(false);
        setShowDiary(true);
    };

    const loadHistory = () => {
        if (!profile) return;
        const saved = localStorage.getItem(userKey('remind_mission_history', profile.id));
        if (saved) {
            try {
                setHistoryEntries((JSON.parse(saved) as MissionHistoryEntry[]).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
            } catch (e) { console.error('Failed to load history', e); }
        }
        setShowHistory(true);
    };

    const handleSaveEntry = () => {
        if (!newEntryText.trim()) return;

        const defaultTitle = `מכתב מספר ${diaryEntries.length + 1} - ${new Date().toLocaleDateString('he-IL')}`;
        const finalTitle = newEntryTitle.trim() || defaultTitle;

        const newEntry = {
            id: Date.now(),
            date: new Date().toISOString(),
            title: finalTitle,
            text: newEntryText
        };

        const updatedEntries = [newEntry, ...diaryEntries];
        setDiaryEntries(updatedEntries);
        if (profile) localStorage.setItem(userKey('remind_diary', profile.id), JSON.stringify(updatedEntries));

        setNewEntryTitle('');
        setNewEntryText('');
        setAddingEntry(false);
    };

    const filteredEntries = diaryEntries.filter(entry =>
        (entry.text.includes(searchQuery) || (entry.title && entry.title.includes(searchQuery))) || new Date(entry.date).toLocaleDateString('he-IL').includes(searchQuery)
    );

    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const handleLogoutConfirm = async () => {
        await supabase.auth.signOut();
        window.location.reload();
    };

    const handleThemeToggle = () => {
        setIsDarkMode(!isDarkMode);
        if (!isDarkMode) {
            document.documentElement.classList.remove('light-mode');
        } else {
            document.documentElement.classList.add('light-mode');
        }
    };

    const handleAvatarSelect = (avatarId: string) => {
        setShowAvatarModal(false);
        updateAvatar(avatarId);
    };

    if (!profile) return null;

    // Calculate Active Level dynamically based on XP
    const currentXP = profile.xp || 0;
    const activeLevel = LEVELS.reduce((acc, level) => {
        return currentXP >= level.minDiamonds ? level.level : acc;
    }, 1);

    // Filter owned items for avatars - Flat list of all owned avatars
    const ownedAvatars = Array.from(new Set(['default', ...(profile.owned_items || [])])).filter(id => id in CRITTERS);

    // Force local persistence for visual stability
    // Force local persistence for visual stability
    const currentAvatar = localStorage.getItem('remind_avatar') || profile.avatar_id || 'default';



    return (
        <div className={`h-full flex flex-col p-6 overflow-y-auto scrollbar-hide dir-rtl transition-colors duration-300 ${!isDarkMode ? 'bg-white text-foreground' : 'bg-background text-foreground'}`}>

            {/* Header Section */}
            <div className="flex justify-between items-start mb-8">
                <button onClick={handleThemeToggle} className={`p-2 rounded-full ${!isDarkMode ? 'bg-muted text-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
                </button>
                <button onClick={handleLogoutClick} className="text-red-400 hover:bg-muted/50 p-2 rounded-full">
                    <LogOut size={20} />
                </button>
            </div>

            <div className="flex flex-col items-center mb-10">
                <div className="relative group">
                    <div className="w-32 h-32 rounded-full bg-bridge-soft flex items-center justify-center shadow-2xl border-4 border-border mb-4 cursor-pointer" onClick={() => setShowAvatarModal(true)}>
                        <Critter id={currentAvatar} size={104} />
                    </div>
                    <button
                        onClick={() => setShowAvatarModal(true)}
                        className="absolute bottom-4 right-0 bg-primary text-white p-2 rounded-full border-4 border-border shadow-lg hover:scale-110 transition-transform"
                    >
                        <Edit2 size={16} />
                    </button>

                </div>

                <div className="text-center mt-2">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-2 justify-center">
                            {profile.full_name || profile.username}
                        </h1>
                        <p className="text-lg text-muted-foreground dir-ltr text-center">@{profile.username}</p>
                    </div>
                    <p className={`text-sm ${!isDarkMode ? 'text-muted-foreground' : 'text-muted-foreground'} mt-1`}>חבר ב-ReMind מאז 2025</p>
                </div>
            </div>

            {/* Stats Row */}
            <div className="flex justify-center gap-12 mb-10 text-center flex-row-reverse">
                <div>
                    <div className={`${(String(profile.xp || 0).length >= 5) ? 'text-xl' : (String(profile.xp || 0).length === 4) ? 'text-2xl' : 'text-3xl'} font-bold bg-gradient-to-tr from-primary to-accent bg-clip-text text-transparent transition-all duration-300`}>
                        {profile.xp || 0}
                    </div>
                    <div className={`text-xs font-bold ${!isDarkMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                        <div>יהלומים</div>
                        <div>שנצברו</div>
                    </div>
                </div>
                <div>
                    <div className="text-3xl font-bold bg-gradient-to-tr from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                        {activeLevel}
                    </div>
                    <div className={`text-xs font-bold ${!isDarkMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>רמה נוכחית</div>
                </div>
                <div>
                    <div className="text-3xl font-bold bg-gradient-to-tr from-orange-500 to-red-500 bg-clip-text text-transparent">
                        {profile.streak}
                    </div>
                    <div className={`text-xs font-bold ${!isDarkMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>ימי רצף</div>
                </div>
            </div>

            <div className="space-y-4">
                {/* My Diary Button */}
                <div
                    onClick={loadDiary}
                    className={`p-4 rounded-xl flex items-center justify-between cursor-pointer transition-colors ${!isDarkMode ? 'bg-muted hover:bg-muted' : 'bg-card hover:bg-muted border border-border'}`}
                >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center">
                            <BookOpen size={20} />
                        </div>
                        <span className="font-bold">היומן שלי</span>
                    </div>
                    <BookOpen size={16} className="opacity-50" />
                </div>

                {/* Completed Missions / History Button */}
                <div
                    onClick={loadHistory}
                    className={`p-4 rounded-xl flex items-center justify-between cursor-pointer transition-colors ${!isDarkMode ? 'bg-muted hover:bg-muted' : 'bg-card hover:bg-muted border border-border'}`}
                >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                            <ClipboardCheck size={20} />
                        </div>
                        <span className="font-bold">משימות שביצעתי</span>
                    </div>
                    <ClipboardCheck size={16} className="opacity-50" />
                </div>

                <TherapistCard therapistId={profile.therapist_id} isDarkMode={isDarkMode} />
            </div>

            {/* Diary Modal */}
            {showDiary && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm dir-rtl">
                    <div className={`w-full max-w-md rounded-2xl p-6 h-[80vh] flex flex-col ${!isDarkMode ? 'bg-white text-foreground' : 'bg-card border border-border text-foreground'}`}>
                        <div className="flex justify-between items-center mb-6 shrink-0">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <BookOpen className="text-yellow-400" /> היומן שלי
                            </h3>
                            {!addingEntry && !selectedEntry && (
                                <button
                                    onClick={() => setAddingEntry(true)}
                                    className="bg-yellow-500 hover:bg-yellow-400 text-black p-2 rounded-full transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                                </button>
                            )}
                        </div>

                        {addingEntry ? (
                            <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                                <input
                                    type="text"
                                    placeholder="כותרת (אופציונלי)..."
                                    value={newEntryTitle}
                                    onChange={e => setNewEntryTitle(e.target.value)}
                                    className="w-full bg-muted border border-border rounded-xl p-3 focus:border-yellow-500 focus:outline-none text-foreground text-right shrink-0"
                                />
                                <textarea
                                    placeholder="כתוב כאן..."
                                    value={newEntryText}
                                    onChange={e => setNewEntryText(e.target.value)}
                                    className="flex-1 bg-muted border border-border rounded-xl p-3 focus:border-yellow-500 focus:outline-none text-foreground resize-none text-right custom-scrollbar"
                                />
                                <div className="flex gap-2 shrink-0">
                                    <button onClick={() => setAddingEntry(false)} className="flex-1 bg-muted text-foreground font-bold py-3 rounded-xl hover:bg-muted/70">ביטול</button>
                                    <button onClick={handleSaveEntry} className="flex-1 bg-yellow-500 text-black font-bold py-3 rounded-xl hover:bg-yellow-400">שמור</button>
                                </div>
                            </div>
                        ) : selectedEntry ? (
                            <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                                <div className="bg-muted/50 p-4 rounded-xl border border-border flex-1 overflow-y-auto custom-scrollbar">
                                    <div className="border-b border-border pb-4 mb-4">
                                        <h4 className="text-xl font-bold text-yellow-400">{selectedEntry.title}</h4>
                                        <p className="text-xs text-muted-foreground mt-1">{new Date(selectedEntry.date).toLocaleString('he-IL')}</p>
                                    </div>
                                    <p className="whitespace-pre-wrap text-foreground leading-relaxed text-lg pl-2">
                                        {selectedEntry.text}
                                    </p>
                                </div>
                                <button onClick={() => setSelectedEntry(null)} className="w-full bg-muted text-foreground font-bold py-3 rounded-xl shrink-0">חזור לרשימה</button>
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    placeholder="חפש ביומן..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full bg-muted border border-border rounded-xl p-3 mb-4 focus:border-yellow-500 focus:outline-none text-foreground shrink-0"
                                />

                                <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar">
                                    {filteredEntries.length === 0 ? (
                                        <div className="text-center text-muted-foreground mt-12">
                                            <p>לא נמצאו רשומות</p>
                                            <p className="text-xs mt-2">לחץ על ה- + כדי להוסיף!</p>
                                        </div>
                                    ) : (
                                        filteredEntries.map((entry, index) => (
                                            <div
                                                key={entry.id}
                                                onClick={() => setSelectedEntry(entry)}
                                                className="bg-muted/50 p-4 rounded-xl border border-border cursor-pointer hover:bg-muted hover:scale-[1.02] transition-all"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-bold text-yellow-400 text-lg">
                                                        {entry.title || `מכתב ${filteredEntries.length - index}`}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {new Date(entry.date).toLocaleDateString('he-IL')}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <button onClick={() => setShowDiary(false)} className="w-full bg-muted text-foreground font-bold py-3 rounded-xl mt-4 shrink-0">סגור</button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* History Modal */}
            {showHistory && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm dir-rtl">
                    <div className={`w-[90%] max-w-[340px] rounded-2xl p-6 h-[55vh] shadow-2xl flex flex-col ${!isDarkMode ? 'bg-white text-foreground' : 'bg-card border border-border text-foreground'}`}>
                        <div className="flex justify-between items-center mb-6 shrink-0">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <ClipboardCheck className="text-primary" /> משימות שביצעתי
                            </h3>
                            <div className="text-sm bg-muted px-3 py-1 rounded-full text-muted-foreground">
                                {historyEntries.length} בוצעו
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar px-1">
                            {historyEntries.length === 0 ? (
                                <div className="text-center text-muted-foreground mt-12">
                                    <p>עדיין לא ביצעת משימות.</p>
                                </div>
                            ) : (
                                historyEntries.map((entry, i) => {
                                    const entryDate = new Date(entry.date);
                                    const currentDate = entryDate.toLocaleDateString('he-IL');
                                    const prevDate = i > 0 ? new Date(historyEntries[i - 1].date).toLocaleDateString('he-IL') : null;
                                    const showHeader = i === 0 || currentDate !== prevDate;

                                    return (
                                        <React.Fragment key={i}>
                                            {showHeader && (
                                                <div className="sticky top-0 bg-card/95 backdrop-blur-sm py-3 z-10 flex justify-center mb-1">
                                                    <span className="text-[10px] font-bold text-muted-foreground bg-muted/80 px-4 py-1 rounded-full border border-border">
                                                        {currentDate}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="bg-muted/40 p-3 rounded-xl border border-border/50 flex justify-between items-center mb-2 hover:bg-muted/60 transition-colors">
                                                <div>
                                                    <div className="font-bold text-foreground text-sm mb-0.5">{entry.title}</div>
                                                    <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                                                        {entryDate.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                                <div className="bg-yellow-500/10 text-yellow-400 px-2.5 py-1 rounded-lg text-xs font-bold border border-yellow-500/20 min-w-[50px] text-center">
                                                    +{entry.xp}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>
                        <button onClick={() => setShowHistory(false)} className="w-full bg-muted text-foreground font-bold py-3 rounded-xl mt-4 shrink-0">סגור</button>
                    </div>
                </div>
            )}

            {/* Change Avatar Modal */}
            {showAvatarModal && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4 backdrop-blur-sm">
                    <div className={`w-full max-w-sm rounded-2xl p-6 h-[70vh] flex flex-col ${!isDarkMode ? 'bg-white' : 'bg-card border border-border'}`}>
                        <h3 className="text-xl font-bold mb-4 text-center">בחר אווטאר</h3>
                        <div className="grid grid-cols-4 gap-3 overflow-y-auto flex-1 p-2 content-start">
                            {ownedAvatars.map(key => (
                                <button
                                    key={key}
                                    onClick={() => handleAvatarSelect(key)}
                                    className={`aspect-square text-3xl flex items-center justify-center rounded-xl border-2 transition-all ${currentAvatar === key ? 'border-primary bg-primary/20' : 'border-transparent hover:bg-muted bg-muted/50'}`}
                                >
                                    <Critter id={key} size={56} title={CRITTERS[key]?.name} />
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setShowAvatarModal(false)} className="w-full bg-muted text-foreground font-bold py-3 rounded-xl mt-4 shrink-0">ביטול</button>
                    </div>
                </div>
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm dir-rtl animate-in fade-in duration-200">
                    <div className={`w-[90%] max-w-[320px] rounded-2xl p-6 shadow-2xl transform transition-all scale-100 ${!isDarkMode ? 'bg-white text-foreground' : 'bg-card border border-border text-foreground'}`}>
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mb-4 ring-4 ring-red-500/5">
                                <LogOut size={32} />
                            </div>
                            <h3 className="text-xl font-bold mb-2">מתנתק?</h3>
                            <p className={`text-sm ${!isDarkMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                                האם אתה בטוח שאתה רוצה לצאת מהחשבון?
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowLogoutConfirm(false)}
                                className={`flex-1 font-bold py-3 rounded-xl transition-colors ${!isDarkMode ? 'bg-muted text-muted-foreground hover:bg-muted' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                            >
                                ביטול
                            </button>
                            <button
                                onClick={handleLogoutConfirm}
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-red-500/20 transition-colors"
                            >
                                כן, צא
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

